import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Container,
  Paper,
  Typography,
  Grid,
  Button,
  Box,
  Avatar,
  CircularProgress,
  useTheme,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  AddPhotoAlternate,
  Delete,
  Save,
  ArrowBack,
} from "@mui/icons-material";
import CustomTextField from "../components/RTLTextField";
import {
  addProductFormData,
  editProductFormData,
  getoneProduct,
  resetProduct,
} from "../redux/slices/product/thunk";
import { useDispatch, useSelector } from "react-redux";
import imageCompression from "browser-image-compression";
import notify from "../components/notify";
import LoadingComponent from "../components/LoadingComponent";

const ProductForm = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem("user"));

  const isEdit = Boolean(id);
  const { loading, product, error } = useSelector(
    (state) => state.productSlice
  );

  useEffect(() => {
    if (id) {
      dispatch(getoneProduct({ id }));
    }
    return () => {
      dispatch(resetProduct());
    };
  }, [id, dispatch]);

  // Form validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required(t("productForm.validation.nameRequired")),
    description: Yup.string(),
    price: Yup.number()
      .positive(t("productForm.validation.positivePrice"))
      .required(t("productForm.validation.priceRequired")),
    discount: Yup.number()
      .min(0, t("productForm.validation.minDiscount"))
      .max(100, t("productForm.validation.maxDiscount"))
      .nullable(),
    amount: Yup.number()
      .integer(t("productForm.validation.integerAmount"))
      .min(0, t("productForm.validation.minAmount"))
      .when("isLimited", {
        is: true,
        then: (schema) =>
          schema.required(t("productForm.validation.amountRequired")),
        otherwise: (schema) => schema.notRequired(),
      }),
    isLimited: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      name: product?.name || "",
      description: product?.description || "",
      price: product?.price || "",
      discount: product?.discount || "",
      amount: product?.count_in_stock || "",
      isLimited: Boolean(product?.stock_limit) || false,
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);

      const user = JSON.parse(localStorage.getItem("user"));
      const formData = new FormData();

      formData.append("name", values.name);
      formData.append("description", values.description);
      formData.append("price", values.price);
      formData.append("discount", values.discount || "");
      formData.append("count_in_stock", values.amount);
      formData.append("stock_limit", values.isLimited);
      formData.append("familyId", user?._id);

      try {
        if (imageFile) {
          const compressedFile = await imageCompression(imageFile, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1024,
            useWebWorker: true,
          });
          formData.append("image", compressedFile);
        }

        if (id) {
          await dispatch(editProductFormData({ id, formData })).unwrap();
          notify(t("product_edit_success"), "success");
        } else {
          await dispatch(addProductFormData(formData)).unwrap();
          notify(t("product_add_success"), "success");
          formik.resetForm();
          setImagePreview(null);
          setImageFile(null);
        }
        // navigate('/familyHome');
      } catch (error) {
        console.error("Product Operation Failed:", error);
        notify(t("product_operation_error"), "error");
      } finally {
        setIsLoading(false);
      }
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (product?.image) {
      setImagePreview(product.image);
    }
  }, [product]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageFile(null);
  };

  const handleLimitedChange = (e) => {
    const isLimited = e.target.checked;
    formik.setFieldValue("isLimited", isLimited);
    if (!isLimited) {
      formik.setFieldValue("amount", "");
    }
  };

  const calculateDiscountedPrice = () => {
    if (!formik.values.price || !formik.values.discount) return "";

    const price = parseFloat(formik.values.price);
    const discount = parseFloat(formik.values.discount);

    if (isNaN(price) || isNaN(discount)) return "";

    return (price * (1 - discount / 100)).toFixed(2);
  };

  if (user?.type !== "family") {
    return <Navigate to="/" replace />;
  }
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <LoadingComponent />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBack sx={{ mx: 1 }} />}
        onClick={() => navigate("/familyHome")}
        sx={{ mb: 2, direction: isArabic ? "rtl" : "ltr" }}
      >
        {t("productForm.buttons.back")}
      </Button>

      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ mb: 4, textAlign: isArabic ? "right" : "left" }}
        >
          {isEdit ? t("productForm.title.edit") : t("productForm.title.add")}
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Image Upload Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: `1px dashed ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                  position: "relative",
                  backgroundColor: theme.palette.grey[50],
                }}
              >
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="product-image-upload"
                  type="file"
                  onChange={handleImageChange}
                />

                {imagePreview ? (
                  <>
                    <Avatar
                      src={imagePreview}
                      sx={{
                        width: "100%",
                        height: "auto",
                        margin: "0 auto 16px",
                        border: `2px solid ${theme.palette.primary.main}`,
                      }}
                      variant="rounded"
                    />
                    <Box
                      sx={{ display: "flex", justifyContent: "center", gap: 2 }}
                    >
                      <label htmlFor="product-image-upload">
                        <Button
                          variant="outlined"
                          color="primary"
                          component="span"
                          startIcon={<AddPhotoAlternate sx={{ ml: 1 }} />}
                        >
                          {t("productForm.buttons.changeImage")}
                        </Button>
                      </label>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Delete sx={{ ml: 1 }} />}
                        onClick={handleRemoveImage}
                      >
                        {t("productForm.buttons.removeImage")}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <AddPhotoAlternate
                      sx={{
                        fontSize: 60,
                        color: theme.palette.grey[400],
                        mb: 1,
                      }}
                    />
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      gutterBottom
                    >
                      {t("productForm.image.placeholder")}
                    </Typography>
                    <label htmlFor="product-image-upload">
                      <Button
                        variant="outlined"
                        color="primary"
                        component="span"
                        startIcon={<AddPhotoAlternate sx={{ ml: 1 }} />}
                      >
                        {t("productForm.buttons.uploadImage")}
                      </Button>
                    </label>
                  </>
                )}
              </Box>
            </Grid>

            {/* Product Name */}
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label={t("productForm.fields.name")}
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                isRTL={isArabic}
                required
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label={t("productForm.fields.description")}
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                multiline
                rows={4}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
                isRTL={isArabic}
              />
            </Grid>

            {/* Price */}
            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label={t("productForm.fields.price")}
                name="price"
                type="number"
                value={formik.values.price}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.price && Boolean(formik.errors.price)}
                helperText={formik.touched.price && formik.errors.price}
                required
                isRTL={isArabic}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">{t("rs")}</InputAdornment>
                  ),
                  inputProps: { min: 0, step: 0.01 },
                }}
              />
            </Grid>

            {/* Discount */}
            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label={t("productForm.fields.discount")}
                name="discount"
                type="number"
                value={formik.values.discount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.discount && Boolean(formik.errors.discount)
                }
                helperText={formik.touched.discount && formik.errors.discount}
                isRTL={isArabic}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={{ pl: 1, pr: 1 }}> % </Box>
                    </InputAdornment>
                  ),
                  inputProps: { min: 0, max: 100 },
                }}
              />
            </Grid>

            {/* Discounted Price Display */}
            {formik.values.discount && formik.values.price && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    backgroundColor: theme.palette.primary.mainOwnerBackground,
                    p: 2,
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "row",
                  }}
                >
                  <Typography variant="body1">
                    {t("productForm.labels.discountedPrice")}:
                  </Typography>
                  <Typography variant="h6" sx={{ mx: 1, fontWeight: "bold" }}>
                    {calculateDiscountedPrice()} <span> {t("rs")}</span>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.success.dark,
                      ml: isArabic ? 0 : 1,
                      mr: isArabic ? 1 : 0,
                    }}
                  >
                    ( {t("productForm.labels.youSave")} {formik.values.discount}{" "}
                    % )
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Amount and Limited Stock */}
            <Grid item xs={12}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12} md={!formik.values.isLimited ? 12 : 6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isLimited"
                        checked={formik.values.isLimited}
                        onChange={handleLimitedChange}
                        color="primary"
                      />
                    }
                    label={t("productForm.labels.AddlimitedStock")}
                    sx={{ direction: isArabic ? "rtl" : "ltr" }}
                  />
                </Grid>

                {formik.values.isLimited && (
                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      fullWidth
                      label={t("productForm.fields.amount")}
                      name="amount"
                      type="number"
                      value={formik.values.amount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.amount && Boolean(formik.errors.amount)
                      }
                      helperText={formik.touched.amount && formik.errors.amount}
                      isRTL={isArabic}
                      required
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={isLoading}
                fullWidth
                startIcon={
                  isLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Save sx={{ ml: 1 }} />
                  )
                }
                sx={{
                  px: 4,
                  py: 1.5,
                  minWidth: 100,
                  direction: isArabic ? "rtl" : "ltr",
                }}
              >
                {isLoading
                  ? t("productForm.buttons.saving")
                  : isEdit
                  ? t("productForm.buttons.update")
                  : t("productForm.buttons.save")}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default ProductForm;
